import { Row, Size } from '@busybusy/webapp-react-ui';
import { CloseIcon } from 'assets/icons';
import classNames from 'classnames';
import BreakForm, { IBreakFormData } from 'components/domain/time-entry-break/BreakForm/BreakForm';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import { useOpenable } from 'hooks';
import _ from 'lodash';
import { DateTime, Duration } from 'luxon';
import * as React from 'react';
import { useState } from 'react';
import { ClassName } from 'types/ClassName';
import IIdable from 'types/Idable';
import ITimeRange from 'types/TimeRange';
import { dateUtils } from 'utils';
import {
  getTimezoneSupForDate,
  getTotalMillisFromTimeRangeDateTimeType,
  nightShiftAdjustment,
  setZone,
  timeInRange,
} from 'utils/dateUtils';
import { t } from 'utils/localize';
import { uuid } from 'utils/uuidUtils';
import MultiPicker from '../../../foundation/pickers/MultiPicker/MultiPicker';
import './BreakMultiPicker.scss';

export interface IBreakMultiPickerProps {
  timeEntryStart?: DateTime | null;
  timeEntryEnd?: DateTime | null;
  timeFormat?: string;
  breaks: IBreakMultiPickerItem[];
  displayDateOnEntryDifference?: boolean;
  hideDateOnBreakForm?: boolean;
  displayError?: (item: IBreakMultiPickerItem) => boolean;
  onChange: (breaks: IBreakMultiPickerItem[]) => void;
  timezone?: string | null;
  className?: ClassName;
}

export interface IBreakMultiPickerItem extends IIdable<string> {
  timeRange: ITimeRange<DateTime>;
  isOpen?: boolean;
  startDst?: boolean | null;
  endDst?: boolean | null;
}

function BreakMultiPicker(props: IBreakMultiPickerProps) {
  const {
    timeEntryStart,
    timeEntryEnd,
    timeFormat = 't',
    breaks,
    displayDateOnEntryDifference = true,
    displayError,
    onChange,
    hideDateOnBreakForm,
    timezone,
    className,
  } = props;

  const [editingBreak, setEditingBreak] = useState<IBreakMultiPickerItem | null>(null);
  const { isOpen: breakDialogOpen, open: openBreakDialog, close: closeBreakDialog } = useOpenable();
  const sortedBreaks = _.sortBy(breaks, (brk) => brk.timeRange.startTime.toSeconds());

  const toDisplayBreak = (item: IBreakMultiPickerItem): IBreakMultiPickerItem => {
    if (!timezone) {
      return item;
    }
    const startTime = setZone(item.timeRange.startTime, timezone, true);
    const endTime = setZone(item.timeRange.endTime, timezone, true);
    return { ...item, timeRange: { startTime, endTime }, startDst: startTime.isInDST, endDst: endTime.isInDST };
  };
  const displayTimeEntryStart = timezone && timeEntryStart ? setZone(timeEntryStart, timezone, true) : timeEntryStart;
  const displayTimeEntryEnd = timezone && timeEntryEnd ? setZone(timeEntryEnd, timezone, true) : timeEntryEnd;

  function renderItem(value: IBreakMultiPickerItem, index: number) {
    const displayValue = toDisplayBreak(value);
    function onItemClick() {
      if (value.isOpen) {
        // We don't edit open breaks burh
        return;
      }
      setEditingBreak(displayValue);
      openBreakDialog();
    }

    function onDeleteItem(event: React.MouseEvent) {
      event.stopPropagation();
      onChange(breaks.filter((brk) => brk.id !== value.id));
    }

    const total = getTotalMillisFromTimeRangeDateTimeType(displayValue.timeRange, true);
    if (total !== null) {
      let breakItemClasses = 'break-item';
      const nightShiftedAdjustment = nightShiftAdjustment(displayValue.timeRange);
      if (
        (displayError && displayError(value)) ||
        (!displayError &&
          displayTimeEntryStart &&
          displayTimeEntryEnd &&
          !timeInRange(
            nightShiftedAdjustment.startTime,
            { startTime: displayTimeEntryStart, endTime: displayTimeEntryEnd },
            true
          ) &&
          !timeInRange(
            nightShiftedAdjustment.endTime,
            { startTime: displayTimeEntryStart, endTime: displayTimeEntryEnd },
            true
          ))
      ) {
        breakItemClasses = 'break-item input-error';
      }

      const withMarginClasses = classNames(breakItemClasses, {
        'mt-4': index !== 0,
      });

      const showDate = (isStart: boolean): boolean => {
        if (!displayDateOnEntryDifference) {
          return false;
        } else if (
          displayTimeEntryStart &&
          displayTimeEntryEnd &&
          dateUtils.isSameDay(displayTimeEntryStart, displayTimeEntryEnd)
        ) {
          return false;
        } else if (isStart && dateUtils.isSameDay(displayValue.timeRange.startTime, displayValue.timeRange.endTime)) {
          return false;
        }
        return true;
      };

      const displayStart = displayValue.timeRange.startTime;
      const displayEnd = displayValue.timeRange.endTime;
      const displayStartDst = displayValue.startDst ?? false;
      const displayEndDst = displayValue.endDst ?? false;

      return (
        <div className={withMarginClasses} onClick={onItemClick} key={value.id}>
          <Row className="break-item-left-content">
            <div>{Duration.fromMillis(total).toFormat('h:mm')}</div>
            <div>
              {!showDate(true) ? displayStart.toFormat(timeFormat) : displayStart.toFormat(`${timeFormat} (DD)`)}
              {getTimezoneSupForDate(displayStart, displayStartDst, displayEnd)} -{' '}
              {value.isOpen
                ? '?'
                : !showDate(false)
                  ? displayEnd.toFormat(timeFormat)
                  : displayEnd.toFormat(`${timeFormat} (DD)`)}
              {value.isOpen ? '' : getTimezoneSupForDate(displayEnd, displayEndDst, displayStart)}
            </div>
          </Row>

          {!value.isOpen ? (
            <IconButton onClick={onDeleteItem} svg={CloseIcon} buttonSize={Size.SMALL} size={Size.MEDIUM} />
          ) : (
            <div className="m-3"> </div>
          )}
        </div>
      );
    } else {
      return null;
    }
  }

  function onBreakSubmit(formData: Pick<IBreakFormData, 'timeRange'>) {
    if (editingBreak) {
      onChange(
        breaks.map((brk) => {
          if (brk.id === editingBreak.id) {
            return { ...brk, timeRange: formData.timeRange as ITimeRange<DateTime> };
          } else {
            return brk;
          }
        })
      );
    } else {
      onChange([
        ...breaks,
        { id: uuid(), timeRange: formData.timeRange as ITimeRange<DateTime>, startDst: null, endDst: null },
      ]);
    }

    setEditingBreak(null);
    closeBreakDialog();
  }

  function onDeleteEditingBreak() {
    onChange(breaks.filter((brk) => brk.id !== editingBreak?.id));
    setEditingBreak(null);
    closeBreakDialog();
  }

  const classes = classNames('break-multi-picker', className);

  return (
    <div className={classes}>
      <HeaderDialog
        isOpen={breakDialogOpen}
        onClose={closeBreakDialog}
        title={editingBreak !== null ? t('Edit Break') : t('Create Break')}
      >
        {displayTimeEntryStart && displayTimeEntryEnd && (
          <BreakForm
            className="pt-3 pb-3 px-8"
            timeEntryStart={displayTimeEntryStart}
            timeEntryEnd={displayTimeEntryEnd}
            timeFormat={timeFormat}
            onSubmit={onBreakSubmit}
            onDelete={onDeleteEditingBreak}
            breaks={timezone ? breaks.map(toDisplayBreak) : breaks}
            editingBreak={editingBreak}
            hideDateOnBreakForm={hideDateOnBreakForm}
          />
        )}
      </HeaderDialog>

      <MultiPicker
        onAddClick={() => {
          setEditingBreak(null);
          openBreakDialog();
        }}
        limit={3}
        renderItem={renderItem}
        values={sortedBreaks}
      />
    </div>
  );
}

export default BreakMultiPicker;
